import { useState } from 'react';
import { ImageSquare } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';

/**
 * FRONT-2382 (feedback round 2, live finding) — the one `<img>` used
 * everywhere a design suggestion or a suggestion-kind reference shows a
 * picture: the suggestion grid's card thumbnail, the zoomed viewer, and the
 * Project-context dialog's reference thumbnail. All three share this one
 * component so a fix here reaches every place a web-picked image can fail
 * to load, rather than three copies that can drift.
 *
 * A web-picked suggestion's `imageUrl` points at a foreign CDN. The server
 * fetched the same URL fine when it verified the suggestion (it sends no
 * `Referer` header at all) — but a browser request DOES send one by
 * default, and some CDNs reject a foreign referrer outright. Without
 * `referrerPolicy="no-referrer"` that shows up as a silently broken `<img>`:
 * live-run finding, `d-project-context-dirty.png` — a picked "Soft Paper
 * Grid" reference rendered only its alt text in the Project-context dialog.
 *
 * `onError` covers whatever still fails (a dead link, a CDN timeout) with a
 * neutral, token-coloured tile carrying a plain image glyph — never the
 * native broken-image icon, and never bare alt text with no visual
 * affordance that something is wrong.
 */
export function DesignSuggestionImage({
  src,
  alt,
  className,
  testId,
}: {
  src: string;
  alt: string;
  className?: string;
  testId?: string;
}) {
  // Keyed on the url that failed, not a bare flag: the zoomed viewer keeps
  // one instance mounted while its arrows swap `src`, so a boolean would pin
  // the fallback onto every picture after the first broken one.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (failedSrc === src) {
    return (
      <div
        role="img"
        aria-label={alt}
        data-testid={testId}
        className={cn('flex items-center justify-center bg-muted text-muted-foreground', className)}
      >
        <ImageSquare size={20} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      data-testid={testId}
      referrerPolicy="no-referrer"
      onError={() => setFailedSrc(src)}
      className={className}
    />
  );
}
